export enum FileExtension {
	pdf,
	txt,
	eml,
	msg,
}

export interface RequestedFile {
	fileHash: string;
	downloadUrl: string;
	watermarkText?: string;
	fileExtension: FileExtension;
	viewOnly: boolean;
	signature?: string;
	encryptionKeyId?: string;
	signatureKeyId?: string;
}

interface Configuration {
	origin: string;
	apiPath: string;
}

export interface TransformedFile {
	previewUrl: string;
	fileSize: number;
}

export interface WatermarkFileInformation {
	resources: Resource[];
}

export interface Resource {
	resourceRID: string;
	fileName: string;
	fileSizeInBytes: number;
	createdDate: string;
	createdByRID: string;
	previewUrl?: string;
	fileExtension: string;
	showDownload: boolean;
	description: string;
}

export interface RequestedResourceRIDs {
	resourceRIDs: string[];
}

export interface WatermarkedCreateResponse {
	resourceList: string[];
}

export interface WatermarkRequest {
	resourceList: string[];
}

const configuration: Configuration = {
	origin: window.origin,
	apiPath: '/io/fileviewer/',
};

export function configureHttp(configure: (current: Configuration) => void) {
	configure(configuration);
}

function createRequest(path: string, init: RequestInit): Request {
	const url = `${configuration.origin}${configuration.apiPath}${path}`;
	init.headers = {
		...init.headers,
		'X-BFF-CSRF': 'true',
	};
	return new Request(url, init);
}

export const FilesHttpService = {
	async getPreviewUrlAsync(requestedFile: RequestedFile) {
		const request = createRequest('api/v1/files/transform', {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestedFile),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to preview url.',
		});

		const responseText = await response.text();

		return JSON.parse(responseText);
	},

	async getWatermarkedFileAsync(requestedResourceRIDs: WatermarkRequest) {
		const request = createRequest('api/v1/watermarkfile/read', {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestedResourceRIDs),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get watermarked file information.',
		});

		const responseText = await response.text();

		return JSON.parse(responseText) as WatermarkFileInformation;
	},

	async getWatermarkedFileVersionAsync(requestedResourceRIDs: WatermarkRequest) {
		const request = createRequest('/api/v1/watermarkfileversion/read', {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestedResourceRIDs),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get watermarked file information.',
		});

		const responseText = await response.text();

		return JSON.parse(responseText) as WatermarkFileInformation;
	},

	async postWatermarkFileCreateAsync(requestList: WatermarkRequest) {
		const request = createRequest('api/v1/watermarkfile/create', {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestList),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get tranformed watermarked resource.',
		});

		const responseText = await response.text();

		return JSON.parse(responseText) as WatermarkedCreateResponse;
	},

	async postWatermarkFileVersionCreateAsync(requestList: WatermarkRequest) {
		const request = createRequest('api/v1/watermarkfileversion/create', {
			method: 'POST',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestList),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 200,
			defaultErrorMessage: 'Unable to get tranformed watermarked resource.',
		});

		const responseText = await response.text();

		return JSON.parse(responseText) as WatermarkedCreateResponse;
	},

	async postWatermarkFileDeleteAsync(requestList: WatermarkRequest) {
		const request = createRequest('api/v1/watermarkfile/delete', {
			method: 'DELETE',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestList),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unable to delete watermarked resource.',
		});

		return response.status === 204;
	},

	async postWatermarkFileVersionDeleteAsync(requestList: WatermarkRequest) {
		const request = createRequest('api/v1/watermarkfileversion/delete', {
			method: 'DELETE',
			headers: {
				'content-type': 'application/json',
			},
			body: JSON.stringify(requestList),
		});

		const response = await fetchOrThrowErrorFromResponse(request, {
			expectedStatus: 204,
			defaultErrorMessage: 'Unable to delete watermarked resource.',
		});

		return response.status === 204;
	},
};

async function fetchOrThrowErrorFromResponse(
	request: Request,
	config: { expectedStatus?: number; defaultErrorMessage?: string } = {}
) {
	const response = await fetch(request);

	const isError =
		config.expectedStatus !== undefined
			? response.status !== config.expectedStatus
			: response.status > 399;

	if (isError) {
		throw new Error(config.defaultErrorMessage);
	}

	return response;
}
